/** @format */

import { useSyncExternalStore } from "react";

export interface PlaybackTickerState {
  timestamp: number | null;
  lapProgress: number | null;
}

type Listener = () => void;

type PlaybackTickerUpdate =
  | Partial<PlaybackTickerState>
  | ((prev: PlaybackTickerState) => Partial<PlaybackTickerState>);

let state: PlaybackTickerState = {
  timestamp: null,
  lapProgress: null,
};

const listeners = new Set<Listener>();

const shallowEqual = (next: PlaybackTickerState, prev: PlaybackTickerState): boolean =>
  next.timestamp === prev.timestamp && next.lapProgress === prev.lapProgress;

const notify = () => {
  for (const listener of listeners) {
    listener();
  }
};

const applyState = (next: PlaybackTickerState) => {
  if (shallowEqual(next, state)) {
    return;
  }
  state = next;
  notify();
};

export const setPlaybackTickerState = (update: PlaybackTickerUpdate) => {
  const partial = typeof update === "function" ? update(state) : update;
  const nextState: PlaybackTickerState = {
    ...state,
    ...partial,
  };
  applyState(nextState);
};

export const resetPlaybackTickerState = () => {
  applyState({
    timestamp: null,
    lapProgress: null,
  });
};

export const getPlaybackTickerState = (): PlaybackTickerState => state;

export const subscribePlaybackTicker = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => state;

export const usePlaybackTicker = (): PlaybackTickerState => {
  return useSyncExternalStore(subscribePlaybackTicker, getSnapshot, getSnapshot);
};
